import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook para verificar se o usuário é master
 * ✅ Preferência: função RPC is_master() (SECURITY DEFINER)
 * Fallback: tabela user_roles (quando permitido pelas policies)
 */
export const useIsMaster = () => {
  const { user, session, loading: authLoading } = useAuth();
  const [isMaster, setIsMaster] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkMasterStatus = useCallback(async () => {
    if (!user || !session) {
      setIsMaster(false);
      setLoading(false);
      return;
    }

    try {
      // ✅ SEGURO: Usa função RPC is_master() (SECURITY DEFINER)
      const { data, error } = await supabase.rpc('is_master', {
        p_user_id: user.id,
      });

      if (!error) {
        console.log('✅ useIsMaster: RPC result =', data);
        setIsMaster(data === true);
        return;
      }

      // Alguns ambientes podem ter assinatura diferente (user_id)
      const { data: altData, error: altError } = await (supabase as any).rpc('is_master', {
        user_id: user.id,
      });

      if (!altError) {
        console.log('✅ useIsMaster: RPC (alt) result =', altData);
        setIsMaster(altData === true);
        return;
      }

      console.log('⚠️ useIsMaster: RPC failed, trying fallback', error);

      // Fallback: verificar diretamente na tabela user_roles
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'master')
        .maybeSingle();

      if (roleError && roleError.code !== 'PGRST116') {
        console.error('Error checking master role:', roleError);
        setIsMaster(false);
      } else {
        console.log('✅ useIsMaster: Fallback result =', !!roleData);
        setIsMaster(!!roleData);
      }
    } catch (err) {
      console.error('Exception checking master status:', err);
      setIsMaster(false);
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user || !session) {
      setIsMaster(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    checkMasterStatus();

    // Reagir a mudanças de role (quando disponível)
    const channel = supabase
      .channel('user_roles_changes_master')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          checkMasterStatus();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [user, session, authLoading, checkMasterStatus]);

  return { isMaster, loading };
};

